import {
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import {
	CarrierFeedback,
	CarrierNotes,
	GET_CARRIER_BUSINESS_NAME,
	IS_NOT_NULL_OR_UNDEFINED,
	RestService,
	TODAY,
	User,
} from 'cargo-chief-commons';
import moment from 'moment';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { ACCOUNT_TYPE } from '@domain/constants/app-variables';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';
import { ContentChange } from 'ngx-quill';
import Quill from 'quill';
import { UrlService } from '@service/url/url.service';
import { UtilService } from '@service/util/util.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { ConfirmationDialogComponent } from '../../common/confirmation-dialog/confirmation-dialog.component';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { SegmentWrapperService } from '@service/segmentWrapper/segment-wrapper.service';
import { SegmentEventsConstants } from '@domain/constants/segment-events-constants';
import { takeUntil } from 'rxjs/operators';
import { NoteType } from '@notes/note-list/note-list.component';
import { BaseComponent } from '@shared/base/base.component';
import { DateTime } from 'luxon';

enum FeedbackType {
	Rating,
	Question,
}

export interface FeedbackInformation {
	originCity: string;
	originState: string;
	destCity: string;
	destState: string;
	trailerTypes: string;
	truckId: string;
	truckSearchId: string;
	truckLaneSearchId: string;
	truckType: string;
}

interface FeedbackQuestion {
	readonly formattedName: string;
	readonly condensedName: string;
	readonly type: FeedbackType;
	answer: any;
}

@Component({
	selector: 'app-note',
	templateUrl: './note.component.html',
	styleUrls: ['./note.component.scss'],
})
export class NoteComponent extends BaseComponent implements OnInit, OnChanges {
	@Input() noteType = NoteType.CARRIER;
	@Input() noteId: string | null;
	@Input() searchTruckForm = null;
	@Input() truckLaneId: string = null;
	@Input() id: string = null;
	@Input() supportId: string = '';
	@Input() name: string;
	@Input() createdDate: Date = TODAY().toDate();
	@Input() modifiedDate: number;
	@Input() feedbackInformation: FeedbackInformation;
	@Input() showFeedback: boolean;
	@Input() defaultContent: string = '';
	@Input() edit: boolean;
	@Input() active: boolean;
	@Input() feedback: CarrierFeedback | null;
	@Input() noteEditAndDeleteEnabled: boolean = true;
	@Input() user: User;
	@Input() origin:string | null;
	@Input() destination:string | null;

	@Output() editClicked: EventEmitter<any> = new EventEmitter();
	@Output() cancelClicked: EventEmitter<any> = new EventEmitter();
	@Output() noteAdded: EventEmitter<any> = new EventEmitter();
	@Output() noteDeleted: EventEmitter<any> = new EventEmitter();
	@Output() feedbackAdded: EventEmitter<any> = new EventEmitter();

	IS_NOT_NULL_OR_UNDEFINED = IS_NOT_NULL_OR_UNDEFINED;

	formattedCreationDate: string;
	savable: boolean;
	expanded: boolean;
	loading: boolean;
	lines: string[] = [];

	feedbackQuestionMap: { [key: string]: FeedbackQuestion } = {
		rating: {
			formattedName: 'How was the conversation?',
			condensedName: 'Rating: ',
			type: FeedbackType.Rating,
			answer: null,
		},
		hasCapacity: {
			formattedName: 'Did they have capacity?',
			condensedName: 'Capacity: ',
			type: FeedbackType.Question,
			answer: null,
		},
	};

	content: UntypedFormControl;

	constructor(
		private ref: ChangeDetectorRef,
		private dialog: MatDialog,
		private cargoChief: Cargochief,
		public accountSettings: AccountSettingsService,
		private urlService: UrlService,
		private restService: RestService,
		private utilService: UtilService,
		private segmentWrapper: SegmentWrapperService
	) {
		super();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes?.supportId && !this.name && this.noteType === NoteType.PROJECT && this.supportId) {
			this.restService
				.POST(this.urlService.getCarriersByIds(), { carrierIDs: [this.supportId] })
				.pipe(takeUntil(this.isDestroyed$))
				.subscribe((result) => {
					const carrier = result.data.content[0];
					this.name = GET_CARRIER_BUSINESS_NAME(carrier?.businessName, carrier?.doingBusinessAs);
				});
		}
	}

	ngOnInit(): void {
		this.formattedCreationDate = moment(this.createdDate).format('MM/DD/YY');
		this.content = new UntypedFormControl(this.defaultContent);
		this.updateLines();

		this.cancelClicked.pipe(takeUntil(this.isDestroyed$)).subscribe((editSave: boolean | null) => {
			this.resetForm(!!editSave);
		});
	}

	private updateLines(): void {
		let filteredContent = this.content.value.replace(/<\/p>/g, '\n');
		filteredContent = filteredContent.replace(/<p>|<br>/g, '');

		this.lines = filteredContent
			.split('\n')
			.map(
				(line) =>
					new DOMParser().parseFromString(line.trim(), 'text/html').documentElement.textContent
			)
			.filter((line) => !!line.length);
	}

	submitNote(): void {
		const note = {
			carrierId: this.getCarrierId(),
			truckLaneId: this.truckLaneId,
			notes: this.content.value,
			lastEditedBy: this.cargoChief.getUser().firstName,
			projectId: this.getProjectId(),
		};

		const segmentEventPayload = {
			carrierId: this.getCarrierId(),
			truckLaneId: this.truckLaneId,
			user: this.cargoChief.getUser(),
			carrierBusinessName: this.name,
			projectId: this.getProjectId(),
		};

		this.segmentWrapper.fireSegmentEvent(
			SegmentEventsConstants.user_added_note_to_carrier,
			segmentEventPayload
		);

		this.loading = true;
		this.restService
			.POST(this.urlService.addCarrierNotes(), note)
			.pipe(takeUntil(this.isDestroyed$))
			.subscribe(
				(result) => {
					if (!this.submitFeedback(result.data)) {
						this.noteAdded.emit(result.data);
						this.loading = false;
					}
				},
				(error) => {
					this.utilService.showSnackbar(
						error.data || 'Unable to save note...',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
				}
			);
	}

	updateNote(): void {
		const note = {
			carrierId: this.getCarrierId(),
			truckLaneId: this.truckLaneId,
			notes: this.content.value,
			userId: this.cargoChief.USER_ID,
			accountId: this.cargoChief.ACCOUNT_ID,
			projectId: this.getProjectId(),
		};

		this.loading = true;
		const url = this.urlService.updateNote(this.noteId);
		this.restService
			.PUT(url, note)
			.pipe(takeUntil(this.isDestroyed$))
			.subscribe(
				() => {
					this.cancelClicked.emit(true);
					this.loading = false;
				},
				(error) => {
					this.utilService.showSnackbar(
						error.data || 'Unable to update note now...',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
				}
			);
	}

	submitFeedback(note: CarrierNotes): boolean {
		if (
			!this.showFeedback ||
			!Object.values(this.feedbackQuestionMap).some(({ answer }) => answer !== null)
		) {
			this.cancelClicked.emit(true);
			return false;
		}

		const feedback = {
			accountId: this.cargoChief.getAccount().id,
			...this.feedbackInformation,
			contact: {},
			user: {
				id: this.cargoChief.getUser().id,
				firstName: this.cargoChief.getUser().firstName,
			},
			reachedCarrier: false,
			loadNotBookedReason: null,
			carrierId: this.id,
			rating: this.feedbackQuestionMap.rating.answer,
			hasCapacity: this.feedbackQuestionMap.hasCapacity.answer,
			userSuggestion: null,
			noteId: note.id,
		};

		this.restService
			.POST(this.urlService.addCarrierFeedback(), feedback)
			.pipe(takeUntil(this.isDestroyed$))
			.subscribe(
				(result) => {
					this.noteAdded.emit(note);
					this.feedback = result.data;
					this.feedbackAdded.emit(result.data);
					this.cancelClicked.emit(true);
					this.segmentWrapper.fireSegmentEvent(
						SegmentEventsConstants.user_submitted_carrier_feedback,
						feedback
					);
					this.loading = false;
				},
				(error) => {
					this.utilService.showSnackbar(
						error.data || 'Unable to save feedback...',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
				}
			);

		return true;
	}

	public get feedbackType(): typeof FeedbackType {
		return FeedbackType;
	}

	public get isAdmin() {
		return this.cargoChief.USER_ACCOUNT_TYPE === ACCOUNT_TYPE.ADMIN;
	}

	resetForm(editSave: boolean): void {
		if (!this.edit || !editSave) {
			this.content.reset(this.defaultContent);
		}
		Object.values(this.feedbackQuestionMap).forEach((feedbackQuestion) => {
			feedbackQuestion.answer = null;
		});
		this.feedbackQuestionMap = { ...this.feedbackQuestionMap };
	}

	enterPressed(event: Event): void {
		//Stop the enter key from refreshing the page when working in the notes textarea
		event.preventDefault();
		event.stopPropagation();
	}

	onEditorCreated(quill: Quill) {
		quill.focus();
		quill.setSelection(quill.getLength(), 0);
	}

	onContentChanged(change: ContentChange): void {
		const current = this.savable;
		this.savable =
			change.text.trim().length !== 0 &&
			change.text.trim().length !== this.defaultContent.trim().length;
		this.updateLines();

		if (current !== this.savable) {
			this.ref.detectChanges();
		}
	}

	showDeleteNoteConfirmation(): void {
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			width: '350px',
			data: {
				title: 'Delete Confirmation',
				body: `Are you sure to delete this note?`,
				cancelButtonText: 'No',
				ConfirmButtonTest: 'Yes',
			},
		});
		dialogRef.afterClosed().subscribe((result) => {
			if (!this.utilService.isNullOrUndefined(result) && result) {
				this.noteDeleted.emit(this.noteId);

				const url = this.urlService.updateNote(this.noteId);
				this.restService.DELETE(url).subscribe();
			}
		});
	}

	private getCarrierId(): string {
		return this.noteType === NoteType.CARRIER ? this.id : this.supportId;
	}

	private getProjectId(): string {
		return this.noteType === NoteType.PROJECT ? this.id : this.supportId;
	}

	public formatEditTime(): string {
		return DateTime.fromJSDate(new Date(this.modifiedDate)).toFormat('h:mm a');
	}
}
